import { inject, Injectable } from '@angular/core';
import { assert, isDefined, mapToObject } from '@sama/js-core';
import { clone, map, omit } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { cloneDeep } from 'lodash';
import { getShapesForEventTracking } from '../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { ObjectState } from '../../../cross-team-shared/annotation/shared/state/object-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import { BrowserControlService } from '../../../cross-team-shared/misc-services/browser-control.service';
import { keys } from '../../../cross-team-shared/misc-utils/keys';
import { isOrthographicVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import { AbstractAnnotationStore } from '../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { getOutputHelper } from '../../../shared/output-helpers/get-output-helper';
import { EventTrackingValue } from '../../../team-apis/core/event-tracking';
import { outputInfoStoreToken } from '../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager';
import { flipYCoordinate } from '../../outputs/annotation/annotation-shared/fusion/projection/orthographic-orchestrator';
import { ObjectSelectionService } from '../../outputs/annotation/annotation-shared/utils/object-selection.service';
import { OutputKeyboardShortcutService } from '../../outputs/annotation/annotation-shared/utils/output-keyboard-shortcut.service';
import { RootShapeSelectionService } from '../../outputs/annotation/annotation-shared/utils/root-shape-selection.service';

@Injectable()
export class AnnotationCopyService extends InjectableSuperclass {
  private fusionManager = inject(FusionManager);
  private messagingService = inject(MessagingService);
  private rootShapeSelectionService = inject(RootShapeSelectionService);
  private browserControlService = inject(BrowserControlService);
  private eventTrackingService = inject(EventTrackingService);
  private store = inject(AbstractAnnotationStore);
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private keyboardShortcutService = inject(OutputKeyboardShortcutService);
  private outputInfoStore = inject(outputInfoStoreToken);
  private objectSelectionService = inject(ObjectSelectionService);

  startListening() {
    this.subscribeToCopySelectedShapes();

    const outputType = this.outputInfoStore.state().output.type;
    if (getOutputHelper(outputType).isMultiFrameOutput) {
      this.subscribeToCopySelectedShapesAllFrames();
    }
  }

  async copyAnnotations(
    eventTrackingValue: EventTrackingValue,
    isForSingleFrame: boolean,
    keyboardEvent?: KeyboardEvent,
  ) {
    const annotationToCopy = this.buildShapesToCopy(isForSingleFrame);
    const text = JSON.stringify(annotationToCopy);
    const currentClipboardText =
      await this.browserControlService.readClipboardText();
    if (
      text === currentClipboardText ||
      (!annotationToCopy.shapes.length && !annotationToCopy.objects.length)
    ) {
      return;
    }

    keyboardEvent?.preventDefault(); // prevent browser copy
    await this.browserControlService.writeClipboardText(text);

    const { shapes, objects } = annotationToCopy;
    this.trackCopyEvent(eventTrackingValue, shapes, objects);

    this.messagingService.success('Copied');
  }

  private trackCopyEvent(
    eventTrackingValue: EventTrackingValue,
    shapes: ShapeState[],
    objects: ObjectState[],
  ) {
    const shapesByIndex = mapToObject(shapes, (shape) => [shape.index, shape]);
    const indexes = shapes.map((shape) => shape.index);

    this.eventTrackingService.track({
      id: 'copy_annotations',
      bucket: 'navigation',
      ...eventTrackingValue,
      shapes: getShapesForEventTracking(shapesByIndex, indexes),
      objects: objects.map((object) => ({
        id: object.index,
        parentId: object.parentId,
      })),
    });
  }

  private subscribeToCopySelectedShapes() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('copySelectedShapes'),
      ({ keyboardEvent, eventTrackingValue }) =>
        this.copyAnnotations(eventTrackingValue, true, keyboardEvent),
    );
  }

  private subscribeToCopySelectedShapesAllFrames() {
    this.subscribeTo(
      this.keyboardShortcutService.getEvent$('copySelectedShapesAllFrames'),
      ({ keyboardEvent, eventTrackingValue }) =>
        this.copyAnnotations(eventTrackingValue, false, keyboardEvent),
    );
  }

  private buildShapesToCopy(isForSingleFrame: boolean) {
    const shapes = this.rootShapeSelectionService.getSelectedShapes();
    const objects = this.objectSelectionService.getNestedSelectedObjects();
    const { objectSelection, shapeSelection } = this.taskWorkspaceStore.state();

    const clonedShapes = shapes.map((shape) => {
      const cloneShape = this.cloneShape(shape, isForSingleFrame);
      if (keys(shapeSelection).includes(cloneShape.index)) {
        cloneShape.parentId = undefined;
      }
      return cloneShape;
    });

    const clonedObjects = objects.map((object) => {
      const clonedObject = cloneDeep(object);
      if (keys(objectSelection).includes(clonedObject.index)) {
        clonedObject.parentId = undefined;
      }
      return clonedObject;
    });
    return this.buildCopyPasteShapesData(
      clonedShapes,
      clonedObjects,
      isForSingleFrame,
    );
  }

  private buildCopyPasteShapesData(
    shapesToCopy: ShapeState[],
    objectToCopy: ObjectState[],
    isForSingleFrame: boolean,
  ) {
    return {
      meta: { isForSingleFrame, dataType: 'Annotation' },
      shapes: shapesToCopy,
      objects: objectToCopy,
    };
  }

  private cloneShape(shape: ShapeState, isForSingleFrame = true) {
    const newShape = clone(shape);
    if (isForSingleFrame) {
      newShape.key_locations = this.trimmedKeyLocations(newShape);
    }

    newShape.key_locations = map(newShape.key_locations, (location) =>
      omit(location, 'source'),
    );
    return newShape;
  }

  private trimmedKeyLocations(shape: ShapeState) {
    const outputName = shape.outputName;
    assert(
      isDefined(outputName),
      'OutputName should be defined for object projects',
    );
    const location = this.getContentConnector(outputName).determineLocation(
      this.store.state().frameNumber,
      shape.index,
    );

    assert(outputName, 'outputName should be defined for annotation shape');

    const { outputInfoByName } = this.taskWorkspaceStore.state();
    const isOrthographic = isOrthographicVideoOutput(
      outputInfoByName[outputName].output,
    );

    if (!isOrthographic) {
      return [location];
    }

    return [{ ...location, points: location.points.map(flipYCoordinate) }];
  }

  private getContentConnector(outputName: string) {
    // Assume that there is only one fusion per project
    return this.fusionManager.getContentConnector(outputName);
  }
}
